<template>
  <BaseDialog type="basic" :is-open="isOpen" :title="$t('dialog.download.title')" @close="onClose">
    <div class="rounded-medium flex flex-col items-center gap-4">
      <div class="flex flex-row items-center gap-2">
        <BaseIcon class="text-on-surface-variant">comment</BaseIcon>
        <p class="text-body-large text-on-surface-variant">
          {{ $t('dialog.download.description') }}
        </p>
      </div>
      <BaseInputSelect
        :items="
          commentScopeOptions
            .filter((option) => !option.disabled)
            .map((option) => ({
              id: option.value,
              title: $t(`dialog.download.commentScope.${option.value}`),
              disabled: option.disabled,
            }))
        "
        class="rounded-large w-auto! self-end justify-self-end"
        hide-decorators
        :disabled="isDownloading"
        v-model="commentScope"
      />
      <BaseInderterminateProgressIndicator class="w-full" :is-running="isDownloading" />
    </div>
    <template #dismiss>
      <BaseTextButton color="text" size="M" :disabled="isDownloading" @click="onClose">
        {{ $t('common.cancel') }}
      </BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton color="filled" size="M" :disabled="isDownloading" @click="submit">
        {{ $t('dialog.download.confirm') }}
      </BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { DialogPhase } from '@/core/models/dialog/dialog'
  import BaseInputSelect from '@/core/components/base/form-fields/BaseInputSelect.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseInderterminateProgressIndicator from '@/core/components/base/progress-indicator/BaseInderterminateProgressIndicator.vue'

  export type CommentScope = 'all' | 'shared' | 'conversation_notes'

  interface Props {
    isOpen: boolean
    dialogPhase: DialogPhase
    isDownloading?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    isDownloading: false,
  })

  const emits = defineEmits<{
    (e: 'close'): void
    (e: 'submit', commentScope: CommentScope): void
  }>()

  const commentScope = ref<CommentScope>('all')

  const canSelectConversationNotes = computed(
    () =>
      props.dialogPhase === DialogPhase.Conversation || props.dialogPhase === DialogPhase.Conducted,
  )

  const commentScopeOptions = computed(() => [
    { value: 'all' as const, disabled: false },
    { value: 'shared' as const, disabled: false },
    {
      value: 'conversation_notes' as const,
      disabled: !canSelectConversationNotes.value,
    },
  ])

  watch(
    () => props.isOpen,
    (isOpen) => {
      if (isOpen) {
        commentScope.value = 'all'
      }
    },
  )

  watch(canSelectConversationNotes, (isAllowed) => {
    if (!isAllowed && commentScope.value === 'conversation_notes') {
      commentScope.value = 'all'
    }
  })

  const onClose = () => {
    if (props.isDownloading) return
    emits('close')
  }

  const submit = () => {
    if (props.isDownloading) return
    emits('submit', commentScope.value)
  }
</script>
